import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {
  initCommonAction,
  initCommonActionResult,
  initCommonClearAction,
  initCommonClearOne,
  initCommonGetOne,
  initCommonSetOneResult,
} from "../highOrderReducers/commonReducer";
import {
  IGetTariffPayload,
  ITariffInitialState,
  ISetTariffResultPayload,
  IGetSubscriptionPricesPayload,
  ISetSubscriptionPricesResultPayload,
  IUpdateTrialEndDatePayload,
  ISetUpdateTrialEndDateResultPayload,
  ICancelSubscriptionPayload,
  ISetCancelSubscriptionResultPayload,
  IResumeSubscriptionPayload,
  ISetResumeSubscriptionResultPayload,
  ICreateSetupIntentPayload,
  ISetCreateSetupIntentResultPayload,
  IAttachPaymentMethodPayload,
  ISetAttachPaymentMethodResultPayload,
  IUpdateSubscriptionQuantityPayload,
  ISetUpdateSubscriptionQuantityResultPayload,
} from "./tariffReducerTypes";
import {mapTariffEntityToITariff} from "./tariffMappers";
import {nameof} from "../../../utils/helpers/tsHelpers/nameof";

const tariffInitialState: ITariffInitialState = {
  oneItemObject: {
    item: {
      companyTariffPackages: [],
    },
    isLoading: false,
  },
  getTariff: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  getSubscriptionPrices: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
    pricePerSeatCents: undefined,
    pricePerAircraftCents: undefined,
    currency: undefined,
    discountPercent: undefined,
    discountDurationMonths: undefined,
    discountEndsAt: undefined,
  },
  updateTrialEndDate: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  cancelSubscription: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  resumeSubscription: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  createSetupIntent: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
    clientSecret: undefined,
  },
  attachPaymentMethod: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  updateSubscriptionQuantity: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  /** Incremented when subscription changes (seats, planes, renew, cancel) - triggers InvoiceHistory refetch */
  invoiceListRefreshTrigger: 0,
};

export const tariffReducer = createSlice({
  name: 'tariff',
  initialState: tariffInitialState,
  reducers: {
    getTariff(state, {payload}: PayloadAction<IGetTariffPayload>) {
      if (!payload?.silent) {
        initCommonGetOne(state);
      }
      initCommonAction(state, nameof<ITariffInitialState>("getTariff"));
    },
    clearTariff(state) {
      initCommonClearOne(state, () => ({companyTariffPackages: []}));
      initCommonClearAction(state, nameof<ITariffInitialState>("getTariff"));
    },
    setTariffResult(state, {payload}: PayloadAction<ISetTariffResultPayload>) {
      if (payload.item) {
        const mappedTariff = mapTariffEntityToITariff(payload.item);
        state.oneItemObject.item = mappedTariff;
        state.oneItemObject.isLoading = false;
      }
      initCommonActionResult(state, payload, nameof<ITariffInitialState>("getTariff"));
    },
    getSubscriptionPrices(state, {payload}: PayloadAction<IGetSubscriptionPricesPayload>) {
      initCommonAction(state, nameof<ITariffInitialState>("getSubscriptionPrices"));
    },
    clearSubscriptionPrices(state) {
      initCommonClearAction(state, nameof<ITariffInitialState>("getSubscriptionPrices"));
      state.getSubscriptionPrices.pricePerSeatCents = undefined;
      state.getSubscriptionPrices.pricePerAircraftCents = undefined;
      state.getSubscriptionPrices.currency = undefined;
      state.getSubscriptionPrices.discountPercent = undefined;
      state.getSubscriptionPrices.discountDurationMonths = undefined;
      state.getSubscriptionPrices.discountEndsAt = undefined;
    },
    setSubscriptionPricesResult(state, {payload}: PayloadAction<ISetSubscriptionPricesResultPayload>) {
      if (payload.pricePerSeatCents !== undefined) {
        state.getSubscriptionPrices.pricePerSeatCents = payload.pricePerSeatCents;
      }
      if (payload.pricePerAircraftCents !== undefined) {
        state.getSubscriptionPrices.pricePerAircraftCents = payload.pricePerAircraftCents;
      }
      if (payload.currency !== undefined) {
        state.getSubscriptionPrices.currency = payload.currency;
      }
      if (payload.discountPercent !== undefined) {
        state.getSubscriptionPrices.discountPercent = payload.discountPercent;
      }
      if (payload.discountDurationMonths !== undefined) {
        state.getSubscriptionPrices.discountDurationMonths = payload.discountDurationMonths;
      }
      if (payload.discountEndsAt !== undefined) {
        state.getSubscriptionPrices.discountEndsAt = payload.discountEndsAt;
      }
      initCommonActionResult(state, payload, nameof<ITariffInitialState>("getSubscriptionPrices"));
    },
    updateTrialEndDate(state, {payload}: PayloadAction<IUpdateTrialEndDatePayload>) {
      initCommonAction(state, nameof<ITariffInitialState>("updateTrialEndDate"));
    },
    clearUpdateTrialEndDate(state) {
      initCommonClearAction(state, nameof<ITariffInitialState>("updateTrialEndDate"));
    },
    setUpdateTrialEndDateResult(state, {payload}: PayloadAction<ISetUpdateTrialEndDateResultPayload>) {
      initCommonActionResult(state, payload, nameof<ITariffInitialState>("updateTrialEndDate"));
    },
    cancelSubscription(state, {payload}: PayloadAction<ICancelSubscriptionPayload>) {
      initCommonAction(state, nameof<ITariffInitialState>("cancelSubscription"));
    },
    clearCancelSubscription(state) {
      initCommonClearAction(state, nameof<ITariffInitialState>("cancelSubscription"));
    },
    setCancelSubscriptionResult(state, {payload}: PayloadAction<ISetCancelSubscriptionResultPayload>) {
      initCommonActionResult(state, payload, nameof<ITariffInitialState>("cancelSubscription"));
    },
    resumeSubscription(state, {payload}: PayloadAction<IResumeSubscriptionPayload>) {
      initCommonAction(state, nameof<ITariffInitialState>("resumeSubscription"));
    },
    clearResumeSubscription(state) {
      initCommonClearAction(state, nameof<ITariffInitialState>("resumeSubscription"));
    },
    setResumeSubscriptionResult(state, {payload}: PayloadAction<ISetResumeSubscriptionResultPayload>) {
      initCommonActionResult(state, payload, nameof<ITariffInitialState>("resumeSubscription"));
    },
    createSetupIntent(state, {payload}: PayloadAction<ICreateSetupIntentPayload>) {
      initCommonAction(state, nameof<ITariffInitialState>("createSetupIntent"));
    },
    clearCreateSetupIntent(state) {
      initCommonClearAction(state, nameof<ITariffInitialState>("createSetupIntent"));
      state.createSetupIntent.clientSecret = undefined;
    },
    setCreateSetupIntentResult(state, {payload}: PayloadAction<ISetCreateSetupIntentResultPayload>) {
      // Store clientSecret in state
      if (payload.clientSecret) {
        state.createSetupIntent.clientSecret = payload.clientSecret;
      } else {
        state.createSetupIntent.clientSecret = undefined;
      }
      initCommonActionResult(state, payload, nameof<ITariffInitialState>("createSetupIntent"));
    },
    attachPaymentMethod(state, {payload}: PayloadAction<IAttachPaymentMethodPayload>) {
      initCommonAction(state, nameof<ITariffInitialState>("attachPaymentMethod"));
    },
    clearAttachPaymentMethod(state) {
      initCommonClearAction(state, nameof<ITariffInitialState>("attachPaymentMethod"));
    },
    setAttachPaymentMethodResult(state, {payload}: PayloadAction<ISetAttachPaymentMethodResultPayload>) {
      initCommonActionResult(state, payload, nameof<ITariffInitialState>("attachPaymentMethod"));
    },
    updateSubscriptionQuantity(state, {payload}: PayloadAction<IUpdateSubscriptionQuantityPayload>) {
      initCommonAction(state, nameof<ITariffInitialState>("updateSubscriptionQuantity"));
    },
    clearUpdateSubscriptionQuantity(state) {
      initCommonClearAction(state, nameof<ITariffInitialState>("updateSubscriptionQuantity"));
    },
    setUpdateSubscriptionQuantityResult(state, {payload}: PayloadAction<ISetUpdateSubscriptionQuantityResultPayload>) {
      initCommonActionResult(state, payload, nameof<ITariffInitialState>("updateSubscriptionQuantity"));
    },
    refreshInvoiceList(state) {
      state.invoiceListRefreshTrigger = (state.invoiceListRefreshTrigger ?? 0) + 1;
    },
  },
});

export const {
  getTariff,
  clearTariff,
  setTariffResult,
  getSubscriptionPrices,
  clearSubscriptionPrices,
  setSubscriptionPricesResult,
  updateTrialEndDate,
  clearUpdateTrialEndDate,
  setUpdateTrialEndDateResult,
  cancelSubscription,
  clearCancelSubscription,
  setCancelSubscriptionResult,
  resumeSubscription,
  clearResumeSubscription,
  setResumeSubscriptionResult,
  createSetupIntent,
  clearCreateSetupIntent,
  setCreateSetupIntentResult,
  attachPaymentMethod,
  clearAttachPaymentMethod,
  setAttachPaymentMethodResult,
  updateSubscriptionQuantity,
  clearUpdateSubscriptionQuantity,
  setUpdateSubscriptionQuantityResult,
  refreshInvoiceList,
} = tariffReducer.actions;

